import * as React from 'react'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import { getTripEventTypeName } from '@/app/common/converters/newDelegation/tripEventTypeToTextConverter'
import { getTripVehicleTypeName } from '@/app/common/converters/newDelegation/tripVehicleTypeToTextConverter'

import EventsRowItem from './EventsRowItem'
import EventsActionBar from './EventsActionBar'
import styles from './BusinessTripEditEvents.module.css'

interface BusinessTripEditEventsProps {
  tripData: any
  onUpdate: (field: string, value: any) => void
  onUpdateArrayItem?: (
    arrayName: string,
    index: number,
    updates: any,
    baseline?: any,
  ) => void
  onAddArrayItem?: (arrayName: string, newItem: any) => void
  onRemoveArrayItems?: (arrayName: string, indices: number[]) => void
  companyId: string
  driverId: string
  tripId: string
  validationErrors?: Record<string, any>
  wrongIndices?: Set<number>
}

const getEventsColumns = (t: (key: string) => string): TableHeaderColumn[] => [
  {
    title: t('businessTrip.col-name'),
    key: 'name',
    type: 'string',
    sort: false,
  },
  { title: t('common.date'), key: 'date', type: 'string', sort: false },
  { title: t('common.time'), key: 'time', type: 'string', sort: false },
  { title: t('common.country'), key: 'country', type: 'string', sort: false },
  { title: t('common.city'), key: 'city', type: 'string', sort: false },
  {
    title: t('businessTrip.col-vehicle-type'),
    key: 'vehicleType',
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.col-reg-number'),
    key: 'registrationNumber',
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.col-doc-number'),
    key: 'docNumber',
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.col-comments'),
    key: 'comments',
    type: 'string',
    sort: false,
  },
  {
    title: '',
    key: 'transit',
    type: 'string',
    sort: false,
  },
]

const BusinessTripEditEvents: React.FC<BusinessTripEditEventsProps> = ({
  tripData,
  onUpdate,
  onUpdateArrayItem,
  onAddArrayItem,
  onRemoveArrayItems,
  companyId,
  driverId,
  tripId,
  validationErrors = {},
  wrongIndices,
}) => {
  const { t } = useLocalize()
  const EventsColumns = getEventsColumns(t)

  const { getCountriesWithMinimalPayment } = useBusinessTripService()
  const [searchValue, setSearchValue] = React.useState('')
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const [sortConfig, setSortConfig] = React.useState<{
    key: string
    direction: 'asc' | 'desc'
  } | null>(null)
  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearchValue('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  // Fetch countries with minimal payment
  const [countriesWithMinimalPayment, setCountriesWithMinimalPayment] =
    React.useState<string[]>([])

  React.useEffect(() => {
    const fetchCountriesWithMinimalPayment = async () => {
      try {
        const data = await getCountriesWithMinimalPayment({ companyId })
        setCountriesWithMinimalPayment(data)
      } catch (err) {
        console.error('Error fetching countries with minimal payment:', err)
      }
    }

    if (companyId) {
      fetchCountriesWithMinimalPayment()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  const events = tripData?.events || []

  const getEventTypeOrder = (tripEvent: TripEventType): number => {
    if (tripEvent === TripEventType.Finish) {
      return Number.MAX_SAFE_INTEGER
    }
    return tripEvent as number
  }

  const sortedEvents = React.useMemo(() => {
    return [...events].sort((a: any, b: any) => {
      // First sort by date
      const dateA = a.date ? new Date(a.date).getTime() : 0
      const dateB = b.date ? new Date(b.date).getTime() : 0

      if (dateA !== dateB) {
        return dateA - dateB
      }

      const orderA = a.tripEvent != null ? getEventTypeOrder(a.tripEvent) : 0
      const orderB = b.tripEvent != null ? getEventTypeOrder(b.tripEvent) : 0

      return orderA - orderB
    })
  }, [events])

  const filteredEvents = React.useMemo(() => {
    let result = sortedEvents

    // Apply search filter
    if (searchValue) {
      const search = searchValue.toLowerCase()
      result = result.filter((event: any) => {
        // Get event type name for search
        const eventTypeName =
          event.tripEvent != null && event.tripEvent !== TripEventType.NotSet
            ? getTripEventTypeName(event.tripEvent, (key: string) =>
                t(key),
              ).toLowerCase()
            : ''
        // Get vehicle type name for search
        const vehicleTypeName =
          event.vehicleType != null &&
          event.vehicleType !== TripVehicleType.NotSet
            ? getTripVehicleTypeName(event.vehicleType, (key: string) =>
                t(key),
              ).toLowerCase()
            : ''

        return (
          eventTypeName.includes(search) ||
          vehicleTypeName.includes(search) ||
          event.city?.toLowerCase().includes(search) ||
          event.country?.name?.toLowerCase().includes(search) ||
          event.docNumber?.toLowerCase().includes(search) ||
          event.comments?.toLowerCase().includes(search)
        )
      })
    }

    // Apply table column sorting if configured
    if (sortConfig) {
      result = [...result].sort((a: any, b: any) => {
        let aValue: any
        let bValue: any

        switch (sortConfig.key) {
          case 'name':
            // Sort by event type name
            aValue =
              a.tripEvent != null && a.tripEvent !== TripEventType.NotSet
                ? getTripEventTypeName(a.tripEvent, (key: string) =>
                    t(key),
                  ).toLowerCase()
                : ''
            bValue =
              b.tripEvent != null && b.tripEvent !== TripEventType.NotSet
                ? getTripEventTypeName(b.tripEvent, (key: string) =>
                    t(key),
                  ).toLowerCase()
                : ''
            break
          case 'date':
            aValue = a.date ? new Date(a.date).getTime() : 0
            bValue = b.date ? new Date(b.date).getTime() : 0
            break
          case 'country':
            aValue = a.country?.name?.toLowerCase() || ''
            bValue = b.country?.name?.toLowerCase() || ''
            break
          case 'city':
            aValue = (a.city || '').toLowerCase()
            bValue = (b.city || '').toLowerCase()
            break
          case 'vehicleType':
            aValue =
              a.vehicleType != null && a.vehicleType !== TripVehicleType.NotSet
                ? getTripVehicleTypeName(a.vehicleType, (key: string) =>
                    t(key),
                  ).toLowerCase()
                : ''
            bValue =
              b.vehicleType != null && b.vehicleType !== TripVehicleType.NotSet
                ? getTripVehicleTypeName(b.vehicleType, (key: string) =>
                    t(key),
                  ).toLowerCase()
                : ''
            break
          case 'registrationNumber':
            aValue = (a.registrationNumber || '').toLowerCase()
            bValue = (b.registrationNumber || '').toLowerCase()
            break
          case 'transit':
            aValue = a.isTransit ? 'tak' : 'nie'
            bValue = b.isTransit ? 'tak' : 'nie'
            break
          case 'docNumber':
            aValue = (a.docNumber || '').toLowerCase()
            bValue = (b.docNumber || '').toLowerCase()
            break
          case 'comments':
            aValue = (a.comments || '').toLowerCase()
            bValue = (b.comments || '').toLowerCase()
            break
          default:
            return 0
        }

        // Compare values
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }

    return result
  }, [sortedEvents, searchValue, sortConfig, t])

  const getOriginalIndex = (event: any): number => {
    const idx = events.indexOf(event)
    if (idx !== -1) return idx
    return events.findIndex((ev: any) => {
      if (
        ev.id != null &&
        event.id != null &&
        String(ev.id) === String(event.id)
      )
        return true
      if (
        ev.lp != null &&
        event.lp != null &&
        ev.lp !== -1 &&
        event.lp !== -1 &&
        ev.lp === event.lp
      )
        return true
      return false
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const indices = filteredEvents
        .map((e: any) => getOriginalIndex(e))
        .filter((i) => i !== -1)
      setSelectedIds(indices)
    } else {
      setSelectedIds([])
    }
  }

  const [isDragging, setIsDragging] = React.useState(false)
  const selectedIdsRef = React.useRef<number[]>(selectedIds)
  selectedIdsRef.current = selectedIds
  const dragStateRef = React.useRef<{
    potential: boolean
    active: boolean
    startIndex: number | null
    mode: 'select' | 'deselect'
  }>({ potential: false, active: false, startIndex: null, mode: 'select' })

  const applyRowSelection = (index: number, mode: 'select' | 'deselect') => {
    if (index === -1) return
    setSelectedIds((prev) => {
      const has = prev.includes(index)
      if (mode === 'select') return has ? prev : [...prev, index]
      return has ? prev.filter((i) => i !== index) : prev
    })
  }

  const handleSelectChange = (index: number, checked: boolean) => {
    applyRowSelection(index, checked ? 'select' : 'deselect')
  }

  const handleSelectMouseDown = (index: number) => {
    if (index === -1) return
    dragStateRef.current = {
      potential: true,
      active: false,
      startIndex: index,
      mode: 'select',
    }
  }

  const handleRowMouseEnter = (index: number) => {
    const ds = dragStateRef.current
    if (index === -1) return
    if (ds.active) {
      applyRowSelection(index, ds.mode)
      return
    }
    if (ds.potential && ds.startIndex != null && ds.startIndex !== index) {
      // First movement — decide select vs. deselect from the start row's state.
      const mode: 'select' | 'deselect' = selectedIdsRef.current.includes(
        ds.startIndex,
      )
        ? 'deselect'
        : 'select'
      ds.active = true
      ds.potential = false
      ds.mode = mode
      setIsDragging(true)
      applyRowSelection(ds.startIndex, mode)
      applyRowSelection(index, mode)
    }
  }

  React.useEffect(() => {
    const handleMouseUp = () => {
      dragStateRef.current = {
        potential: false,
        active: false,
        startIndex: null,
        mode: dragStateRef.current.mode,
      }
      setIsDragging(false)
    }
    document.addEventListener('mouseup', handleMouseUp)
    return () => document.removeEventListener('mouseup', handleMouseUp)
  }, [])

  const handleHeaderClick = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const isSelectedAny = selectedIds.length > 0

  const eligibleForTransit = (e: any) =>
    e && e.tripEvent === TripEventType.ChangeCountry

  const transitScopeIndices: number[] =
    selectedIds.length > 0
      ? selectedIds.filter((i) => eligibleForTransit(events[i]))
      : events
          .map((_: any, i: number) => i)
          .filter((i: number) => eligibleForTransit(events[i]))

  const allTransitSelected =
    transitScopeIndices.length > 0 &&
    transitScopeIndices.every((i) => events[i].isTransit)
  const someTransitSelected = transitScopeIndices.some(
    (i) => events[i].isTransit,
  )

  const toggleAllTransit = (checked: boolean) => {
    const scope = new Set(transitScopeIndices)
    const updated = events.map((e: any, i: number) =>
      scope.has(i) ? { ...e, isTransit: checked } : e,
    )
    onUpdate('events', updated)
  }

  const headerColumns: TableHeaderColumn[] = EventsColumns.map((col) =>
    col.key === 'transit'
      ? {
          ...col,
          headerContent: (
            <div className="tw-grid tw-place-items-center">
              <input
                type="checkbox"
                checked={allTransitSelected}
                ref={(el) => {
                  if (el) {
                    el.indeterminate =
                      someTransitSelected && !allTransitSelected
                  }
                }}
                onChange={(e) => toggleAllTransit(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                disabled={transitScopeIndices.length === 0}
                title="Wyłącz wszystkie z delegowania"
                className="tw-h-5 tw-w-5 tw-cursor-pointer tw-accent-primary"
              />
            </div>
          ),
        }
      : col,
  )

  return (
    <div className="tw-min-w-0 tw-rounded-lg tw-bg-white tw-pb-4">
      {/* Validation error message */}
      {validationErrors.events && (
        <div className="tw-mx-2 tw-mb-4 tw-rounded-lg tw-border tw-border-red-300 tw-bg-red-50 tw-p-3">
          <p className="tw-text-sm tw-font-medium tw-text-red-800">
            {validationErrors.events}
          </p>
        </div>
      )}

      {/* Action Bar */}
      <EventsActionBar
        selectedIds={selectedIds}
        onUpdate={onUpdate}
        onAddArrayItem={onAddArrayItem}
        setSelectedIds={setSelectedIds}
        events={events}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        filteredEventsLength={filteredEvents.length}
      />

      {/* Events table */}
      <div className="tw-max-h-[70vh] tw-overflow-auto tw-rounded-lg tw-border tw-border-base-300">
        <table
          className={`${styles.table} tw-w-full tw-table-fixed tw-border-collapse ${
            isDragging ? 'tw-select-none' : ''
          }`}
        >
          <colgroup>
            <col className="tw-w-9" />
            <col className="tw-w-[16%]" />
            <col className="tw-w-36" />
            <col className="tw-w-[5.5rem]" />
            <col span={7} />
          </colgroup>
          <TableHeader
            columns={headerColumns}
            fontSize="md"
            isSelectedAny={isSelectedAny}
            maxWidthOff
            rounded
            sortConfig={sortConfig}
            bolder
            disabledToggle={false}
            toggleAll={toggleAll}
            onHeaderClick={handleHeaderClick}
          />
          <tbody>
            {filteredEvents.length > 0 ? (
              filteredEvents.map((event: any, index: number) => {
                const originalIndex = getOriginalIndex(event)
                const isChecked = selectedIds.includes(originalIndex)

                return (
                  <EventsRowItem
                    companyId={companyId}
                    originalIndex={originalIndex}
                    onUpdateArrayItem={onUpdateArrayItem}
                    isChecked={isChecked}
                    onSelectChange={handleSelectChange}
                    onSelectMouseDown={handleSelectMouseDown}
                    onRowMouseEnter={handleRowMouseEnter}
                    event={event}
                    isWrong={wrongIndices?.has(originalIndex)}
                    key={originalIndex !== -1 ? originalIndex : `temp-${index}`}
                  />
                )
              })
            ) : (
              <tr>
                <td
                  colSpan={EventsColumns.length + 1}
                  className="tw-p-8 tw-text-center"
                >
                  <p className="tw-text-content-base tw-opacity-70">
                    {t('businessTrip.no-events')}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default BusinessTripEditEvents
